import classnames from 'classnames';
import styles from './index.module.less';
import { useState, useEffect } from 'react';
import type { SupportedHtmlTags } from '../../../core/types';
import useMediaQuery from '../../../utilities/useMediaQuery';
import useZoomDetection from '../../../utilities/useZoomDetection';
import Element from '../../../core/Element';

interface MediaResult {
  sm: boolean;
  md: boolean;
  lg: boolean;
}

export * from './BreakpointContext';
export type ScreenSize = 'sm' | 'md' | 'lg';

interface Result extends MediaResult {
  current: ScreenSize;
}

interface Props {
  children: (result: Result) => React.ReactNode;
  ssrDefault?: ScreenSize;
  forceJsMode?: boolean;
  SsrWrapperComponent?: keyof SupportedHtmlTags;
  checkZoom?: boolean;
}

const addCurrent = (result: MediaResult): Result => ({
  ...result,
  current: (result.lg && 'lg') || (result.md && 'md') || 'sm',
});

// ssr default is used when ssrModeEnabled is false, but we're rendering on
// the server. This will render the child as though it was on a screen
// with the matching value. This is mostly here for backward compatibility,
// using SSRMode (see below) will probably give you better results
let configurableSsrDefault = 'sm';

export const setSsrDefault = (userDefinedDefault: ScreenSize) => {
  configurableSsrDefault = userDefinedDefault;
  return configurableSsrDefault;
};

const getSsrDefault = () => configurableSsrDefault;

// By default, Breakpoint will automatically detect when it's being rendered
// on the server and render all three breakpoints at the same time, using
// CSS to show only the active breakpoint. If ssrModeEnabled is false, then
// Breakpoint will render according to the "ssrDefault" above when on the server.
// consuming applications can toggle it using the functions below
let useCssModeOnServer = true;

export function disableCssModeOnServer() {
  useCssModeOnServer = false;
}

export function enableCssModeOnServer() {
  useCssModeOnServer = true;
}

// Provides a way to globally opt out of zoom functionality in Breakpoint component
let disableZoom = false;

export function disableZoomGlobally() {
  disableZoom = true;
}

const possibleBreakpoints = [
  {
    sm: true,
    md: false,
    lg: false,
  },
  {
    sm: false,
    md: true,
    lg: false,
  },
  {
    sm: false,
    md: false,
    lg: true,
  },
];

const useIsClient = (): boolean => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return isClient;
};

/**
 * @deprecated
 * DEPRECATION NOTICE:
 * This component should not be used and will be removed [in V2](https://github.aexp.com/amex-eng/aqx-components/tree/next)
 *
 * Please migrate to the `useBreakpoint` hook when possible.
 *
 */
export const Breakpoint = ({
  children,
  ssrDefault,
  forceJsMode,
  SsrWrapperComponent = 'div',
  checkZoom = true,
}: Props) => {
  const isClient = useIsClient();
  const isSM = useMediaQuery('(max-width: 767px)');
  const isMD = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const isLG = useMediaQuery('(min-width: 1024px)');

  const result: MediaResult = {
    sm: isSM,
    md: isMD,
    lg: isLG,
  };
  const defaultBreakpoint = ssrDefault || (getSsrDefault() as ScreenSize);
  const [currentBreakpoint, setCurrentBreakpoint] = useState(defaultBreakpoint);
  const zoomDetected = useZoomDetection();

  if (!disableZoom && checkZoom && typeof window === 'object' && zoomDetected) {
    return (
      <>
        {children({
          sm: currentBreakpoint === 'sm',
          md: currentBreakpoint === 'md',
          lg: currentBreakpoint === 'lg',
          current: currentBreakpoint,
        })}
      </>
    );
  }

  if (!forceJsMode && useCssModeOnServer && !isClient) {
    return (
      <>
        {possibleBreakpoints.map(addCurrent).map((breakpoint) => (
          <Element
            Component={SsrWrapperComponent}
            key={breakpoint.current}
            className={classnames(styles.breakpointChild, {
              [styles.showOnSm]: breakpoint.sm,
              [styles.showOnMd]: breakpoint.md,
              [styles.showOnLg]: breakpoint.lg,
            })}
          >
            {children(breakpoint)}
          </Element>
        ))}
      </>
    );
  }

  const currentResult = addCurrent(result);
  if (currentBreakpoint !== currentResult.current) {
    setCurrentBreakpoint(currentResult.current);
  }
  return <>{children(currentResult)}</>;
};
